import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Check, GraduationCap, Building2, School, Sparkles,
  Landmark, Undo2, ShieldCheck, ClipboardCheck,
} from 'lucide-react';
import { PageMeta } from '@/components/PageMeta';
import { FaqAccordion } from '@kollez/design-system';

/**
 * Pricing (/pricing) — decision-tree format ("Three questions, one plan").
 *
 * Every other marketing page uses a hero → grid → testimonials → FAQ shape.
 * /pricing uses a top-down question-flow instead — the buyer picks a side,
 * then picks a scale, and the page reveals one recommended plan card at
 * the bottom. Currency-free throughout; every CTA routes to "Talk to us"
 * or the appropriate signup wizard.
 *
 * Design spec: docs/design/marketing/_per-page-design-language.md
 * (pricing gets the decision-tree / choose-your-path format).
 */

type Side = 'institution' | 'partner' | 'student' | null;
type InstScale = 'pilot' | 'college' | 'group' | 'university' | null;
type PartnerScale = 'verified' | 'cross-campus' | 'national' | null;

interface Plan {
  key: string;
  name: string;
  tagline: string;
  hint: string;
  cta: string;
  ctaHref: string;
  ctaVariant: 'primary' | 'outline';
  features: string[];
  meta?: string;
}

const INST_PLAN_MAP: Record<Exclude<InstScale, null>, Plan> = {
  pilot: {
    key: 'pilot',
    name: 'Pilot',
    tagline: 'Free · one season',
    hint: 'One college, one placement season (up to 6 months). Continue on any plan without losing anything.',
    cta: 'Start a free pilot',
    ctaHref: '/register/institution?plan=pilot',
    ctaVariant: 'primary',
    features: [
      'One college, up to 1,000 final-year students',
      'Post drives, screen applicants, release offers',
      'Nexie: assessment + mock-interview allowance',
      'Community support',
    ],
    meta: 'Nothing charged today. Upgrade when it earns the upgrade.',
  },
  college: {
    key: 'college',
    name: 'College',
    tagline: 'Talk to us',
    hint: 'Full pipeline for a single college. Priced per college, per year.',
    cta: 'Talk to us',
    ctaHref: '/contact?topic=demo&plan=college',
    ctaVariant: 'primary',
    features: [
      'Full drive + offers + reports pipeline',
      'Assessments module + question bank + Nexie',
      'Nexie drafts NIRF / NAAC / AICTE',
      'Unlimited placement officers + faculty mentors',
      'Email support',
    ],
    meta: 'Government / aided colleges get an automatic discount.',
  },
  group: {
    key: 'group',
    name: 'Group',
    tagline: 'Talk to us',
    hint: 'Several colleges as one operation, with a customer-success manager.',
    cta: 'Talk to us',
    ctaHref: '/contact?topic=demo&plan=group',
    ctaVariant: 'primary',
    features: [
      'Everything in College',
      'Group roll-up at 8 am + per-college targets',
      'Named customer-success manager',
      'Phone + WhatsApp support',
      'Group-wide MoUs and reports',
    ],
    meta: 'Most-chosen for education groups running 5+ colleges.',
  },
  university: {
    key: 'university',
    name: 'University',
    tagline: 'Talk to us',
    hint: '50+ colleges, SSO, dedicated region option.',
    cta: 'Talk to us',
    ctaHref: '/contact?topic=demo&plan=university',
    ctaVariant: 'primary',
    features: [
      'Everything in Group',
      'Affiliated-college portal + registrar workflows',
      'SSO (Google, Microsoft) + SCIM provisioning',
      'Custom audit exports · dedicated named team',
    ],
    meta: 'Purpose-built for universities and multi-institution trusts.',
  },
};

const PARTNER_PLAN_MAP: Record<Exclude<PartnerScale, null>, Plan> = {
  verified: {
    key: 'partner-verified',
    name: 'Verified partner',
    tagline: 'Free from partner campuses',
    hint: 'Post any offering, receive applications from partner colleges, download offer trackers.',
    cta: 'Register as a partner',
    ctaHref: '/register/partner',
    ctaVariant: 'primary',
    features: [
      'Post jobs, internships, training, mentorship',
      'Nexie ranks the shortlist with reasons',
      'Verified candidates, contact hidden until shortlist',
      'ATS-friendly export (Darwinbox / Zoho / Greenhouse / more)',
    ],
    meta: 'Never a fee to students. Ever.',
  },
  'cross-campus': {
    key: 'partner-cross',
    name: 'Cross-campus',
    tagline: 'Talk to us',
    hint: 'Full applicant funnel, Nexie assessments, offer sign-off — priced per campus visit.',
    cta: 'Talk to us',
    ctaHref: '/contact?topic=demo&plan=cross-campus',
    ctaVariant: 'primary',
    features: [
      'Everything in Verified partner',
      'Reach campuses outside your MoU network',
      'Nexie-drafted assessments per drive',
      'Cross-campus offer sign-off + audit trail',
    ],
    meta: 'For companies expanding beyond an existing campus footprint.',
  },
  national: {
    key: 'partner-national',
    name: 'National',
    tagline: 'Talk to us',
    hint: 'Multi-campus season, dedicated CS, integration with your ATS.',
    cta: 'Talk to us',
    ctaHref: '/contact?topic=demo&plan=partner-national',
    ctaVariant: 'primary',
    features: [
      'Everything in Cross-campus',
      'Multi-campus placement seasons',
      'Named CS team',
      'Deep ATS integration + dedicated webhook',
    ],
    meta: 'For MNCs and large training providers running national programmes.',
  },
};

/* ────────── sub-components ────────── */

function StepBadge({ n, active, done }: { n: number; active: boolean; done: boolean }) {
  return (
    <span
      className="flex items-center justify-center rounded-full font-semibold flex-shrink-0"
      style={{
        width: 32, height: 32,
        background: done
          ? 'rgb(var(--ink-success))'
          : active
            ? 'rgb(var(--ink-accent))'
            : 'rgb(var(--bg-tertiary))',
        color: (done || active) ? 'rgb(var(--ink-on-accent))' : 'rgb(var(--ink-tertiary))',
        fontSize: 14,
      }}
    >
      {done ? <Check size={16} /> : n}
    </span>
  );
}

function QuestionCard({
  step, active, done, title, children,
}: {
  step: number; active: boolean; done: boolean;
  title: string; children: React.ReactNode;
}) {
  return (
    <section
      className="card card-pad grid gap-4"
      style={{
        opacity: done ? 0.75 : (active ? 1 : 0.4),
        transition: 'opacity var(--dur-2, 220ms) var(--ease-out)',
      }}
    >
      <div className="flex items-center gap-3">
        <StepBadge n={step} active={active} done={done} />
        <h2 className="h-serif text-xl m-0">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function ChoiceTile({
  icon, label, hint, selected, onClick,
}: {
  icon: React.ReactNode; label: string; hint: string; selected: boolean; onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="card card-pad text-left grid gap-2 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      style={{
        borderColor: selected ? 'rgb(var(--ink-accent))' : undefined,
        boxShadow: selected ? '0 0 0 1px rgb(var(--ink-accent))' : undefined,
        background: selected ? 'rgb(var(--bg-info))' : undefined,
        cursor: 'pointer',
      }}
      aria-pressed={selected}
    >
      <span
        className="flex items-center justify-center rounded-lg"
        style={{
          width: 40, height: 40,
          background: 'rgb(var(--bg-info))',
          color: 'rgb(var(--ink-accent))',
        }}
      >
        {icon}
      </span>
      <div className="font-medium">{label}</div>
      <div className="helper m-0">{hint}</div>
    </button>
  );
}

function PlanReveal({ plan }: { plan: Plan }) {
  return (
    <article className="card card-pad grid gap-3" style={{ borderColor: 'rgb(var(--ink-accent))', boxShadow: '0 0 0 1px rgb(var(--ink-accent))' }}>
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="text-sm font-semibold" style={{ color: 'rgb(var(--ink-accent))' }}>Recommended</div>
          <div className="h-serif text-2xl m-0">{plan.name}</div>
        </div>
        <div className="h-serif" style={{ fontSize: '1.5rem', fontWeight: 500 }}>
          {plan.tagline}
        </div>
      </div>
      <p className="text-ink-secondary m-0">{plan.hint}</p>
      <hr className="border-line-tertiary" />
      <ul className="grid gap-2 text-sm">
        {plan.features.map((f) => (
          <li key={f} className="flex gap-2">
            <Check size={14} className="mt-0.5 text-ink-success flex-shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      {plan.meta && (
        <p className="helper m-0 mt-1">{plan.meta}</p>
      )}
      <div className="flex flex-wrap gap-2 mt-2">
        <Link to={plan.ctaHref} className={`btn ${plan.ctaVariant === 'primary' ? 'btn-primary' : 'btn-outline'}`}>
          {plan.cta}
          <ArrowRight size={14} className="ml-1" />
        </Link>
        <Link to="/contact?topic=demo" className="btn btn-ghost">
          Just talk to us
        </Link>
      </div>
    </article>
  );
}

/* ────────── page ────────── */

export function PricingPage() {
  const [side, setSide] = useState<Side>(null);
  const [instScale, setInstScale] = useState<InstScale>(null);
  const [partnerScale, setPartnerScale] = useState<PartnerScale>(null);

  const step1Done = side !== null;
  const needsStep2 = side === 'institution' || side === 'partner';
  const step2Done = (side === 'institution' && instScale !== null) || (side === 'partner' && partnerScale !== null);
  const step3Active = step1Done && (side === 'student' || step2Done);

  const recommendedPlan: Plan | null =
    side === 'institution' && instScale ? INST_PLAN_MAP[instScale]
    : side === 'partner' && partnerScale ? PARTNER_PLAN_MAP[partnerScale]
    : side === 'student' ? {
        key: 'student',
        name: 'Student',
        tagline: 'Always free',
        hint: 'Students never pay on Kollez. Not for sign-up, tests, mock interviews, or offers.',
        cta: 'Join with your college code',
        ctaHref: '/register/student',
        ctaVariant: 'primary',
        features: [
          "Every drive you're eligible for, with reasons when you're not",
          'Nexie: Career Advisor, Resume Builder, Mock Interviewer',
          'Portable badges you keep for life',
          'DigiLocker-verified marks; offer letters on your phone',
          'WhatsApp community and drive alerts',
        ],
        meta: 'Your college pays for Kollez. If anyone asks you for money to apply, interview or join — tell us and we act within a day.',
      }
    : null;

  return (
    <div style={{ background: 'rgb(var(--bg-secondary))' }}>
      <PageMeta
        title="Pricing"
        description="A plan for every placement cell. Free for every student. Priced per conversation — three questions, one recommended plan, one link to talk to us."
        canonical="/pricing"
      />

      <main className="mx-auto max-w-page-wide px-5 lg:px-10 py-16 lg:py-20 grid gap-6" style={{ /* Wizard inner column stays comfortable-narrow while page container hugs header left edge */ }}>
        <div className="w-full" style={{ maxWidth: 960 }}>
        {/* HERO — no CTAPair, just intent-setting */}
        <header className="grid gap-3">
          <p className="eyebrow flex items-center gap-1.5">
            <Sparkles size={12} />
            Pricing
          </p>
          <h1
            className="text-ink-primary"
            style={{
              fontFamily: 'Newsreader, Georgia, serif',
              fontSize: 'clamp(2.1rem,4.5vw,3rem)',
              lineHeight: 1.05,
              fontWeight: 500,
              margin: 0,
              letterSpacing: '-0.01em',
            }}
          >
            Three questions.<br />
            One recommended plan.
          </h1>
          <p className="subhead" style={{ maxWidth: '52ch' }}>
            Kollez is priced per conversation right now. Answer the three
            questions below — we'll surface the plan we'd recommend and
            pre-fill the demo call.
          </p>
        </header>

        {/* QUESTION 1 — Which side of Kollez are you on? */}
        <QuestionCard step={1} active={!step1Done} done={step1Done} title="Which side of Kollez are you on?">
          <div className="grid gap-3 sm:grid-cols-3">
            <ChoiceTile
              icon={<School size={20} />}
              label="Institution"
              hint="Colleges, groups, universities. Run placements + reports."
              selected={side === 'institution'}
              onClick={() => { setSide('institution'); setPartnerScale(null); }}
            />
            <ChoiceTile
              icon={<Building2 size={20} />}
              label="Hiring partner"
              hint="Companies, training providers, mentors, hackathon hosts."
              selected={side === 'partner'}
              onClick={() => { setSide('partner'); setInstScale(null); }}
            />
            <ChoiceTile
              icon={<GraduationCap size={20} />}
              label="Student"
              hint="Every student, every college. Always free."
              selected={side === 'student'}
              onClick={() => { setSide('student'); setInstScale(null); setPartnerScale(null); }}
            />
          </div>
        </QuestionCard>

        {/* QUESTION 2 — Scale (only for institution + partner) */}
        {needsStep2 && (
          <QuestionCard step={2} active={step1Done && !step2Done} done={step2Done} title={side === 'institution' ? 'How many colleges?' : 'How much reach?'}>
            {side === 'institution' && (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <ChoiceTile
                  icon={<Sparkles size={18} />}
                  label="Just one, try it first"
                  hint="A free pilot for one college, one season."
                  selected={instScale === 'pilot'}
                  onClick={() => setInstScale('pilot')}
                />
                <ChoiceTile
                  icon={<School size={18} />}
                  label="1 college"
                  hint="One campus, full pipeline, priced per year."
                  selected={instScale === 'college'}
                  onClick={() => setInstScale('college')}
                />
                <ChoiceTile
                  icon={<Landmark size={18} />}
                  label="2–49 colleges"
                  hint="A group of colleges as one operation."
                  selected={instScale === 'group'}
                  onClick={() => setInstScale('group')}
                />
                <ChoiceTile
                  icon={<Landmark size={18} />}
                  label="50+ colleges"
                  hint="University or large trust. SSO + dedicated region option."
                  selected={instScale === 'university'}
                  onClick={() => setInstScale('university')}
                />
              </div>
            )}
            {side === 'partner' && (
              <div className="grid gap-3 sm:grid-cols-3">
                <ChoiceTile
                  icon={<ShieldCheck size={18} />}
                  label="Partner campuses only"
                  hint="Colleges that already have an MoU with you."
                  selected={partnerScale === 'verified'}
                  onClick={() => setPartnerScale('verified')}
                />
                <ChoiceTile
                  icon={<Building2 size={18} />}
                  label="Cross-campus"
                  hint="Reach colleges outside your MoU network."
                  selected={partnerScale === 'cross-campus'}
                  onClick={() => setPartnerScale('cross-campus')}
                />
                <ChoiceTile
                  icon={<Landmark size={18} />}
                  label="National reach"
                  hint="Multi-campus season with a dedicated team."
                  selected={partnerScale === 'national'}
                  onClick={() => setPartnerScale('national')}
                />
              </div>
            )}
          </QuestionCard>
        )}

        {/* QUESTION 3 — The recommendation */}
        <QuestionCard step={needsStep2 ? 3 : 2} active={step3Active} done={false} title="Here's what we'd recommend.">
          {recommendedPlan ? (
            <PlanReveal plan={recommendedPlan} />
          ) : (
            <p className="text-ink-tertiary m-0">
              Answer the {needsStep2 ? 'first two questions' : 'first question'} above and we'll surface a plan.
            </p>
          )}
        </QuestionCard>

        {/* Institution-specific notices — appear once side is picked */}
        {side === 'institution' && (
          <div className="grid gap-3 mt-4">
            <div
              className="rounded-lg p-4 flex items-start gap-3"
              style={{ background: 'rgb(var(--bg-info))', color: 'rgb(var(--ink-primary))' }}
            >
              <Landmark size={18} className="mt-0.5 flex-shrink-0" style={{ color: 'rgb(var(--ink-accent))' }} />
              <div className="text-sm">
                <b>Government and aided colleges get a discount on every plan.</b>{' '}
                We apply it on your proforma once we've seen your affiliation or grant-in-aid letter.
              </div>
            </div>
            <div className="rounded-lg p-4 flex items-start gap-3 card">
              <ClipboardCheck size={18} className="mt-0.5 flex-shrink-0" style={{ color: 'rgb(var(--ink-accent))' }} />
              <div className="text-sm flex-1">
                <b>Assessments only?</b>{' '}
                For colleges that run placements elsewhere but want Nexie-run exams, question bank, first-year baselines, and readiness reports.
              </div>
              <Link to="/contact?topic=demo&plan=assess" className="btn btn-outline btn-sm flex-shrink-0">Talk to us</Link>
            </div>
            <div className="rounded-lg p-4 flex items-start gap-3 card">
              <Undo2 size={18} className="mt-0.5 flex-shrink-0" style={{ color: 'rgb(var(--ink-accent))' }} />
              <div className="text-sm">
                <b>Every acceptance has a 10-minute undo window.</b>{' '}
                Every offer runs sign-off checks before it leaves. See <Link className="link" to="/for-institutions">for institutions</Link> for the full picture.
              </div>
            </div>
          </div>
        )}

        {/* Partner-specific notice */}
        {side === 'partner' && (
          <div
            className="rounded-lg p-4 flex items-start gap-3"
            style={{ background: 'rgb(var(--bg-info))', color: 'rgb(var(--ink-primary))' }}
          >
            <ShieldCheck size={18} className="mt-0.5 flex-shrink-0" style={{ color: 'rgb(var(--ink-accent))' }} />
            <div className="text-sm">
              <b>Partners never pay colleges or students through Kollez.</b>{' '}
              Listings that charge candidates a fee are removed. Contact hidden until shortlist.
            </div>
          </div>
        )}

        {/* Student-specific notice */}
        {side === 'student' && (
          <div
            className="rounded-lg p-4 flex items-start gap-3"
            style={{ background: 'rgb(var(--bg-success))', color: 'rgb(var(--ink-primary))' }}
          >
            <Sparkles size={18} className="mt-0.5 flex-shrink-0" style={{ color: 'rgb(var(--ink-success))' }} />
            <div className="text-sm">
              <b>Students never pay on Kollez.</b>{' '}
              Not for sign-up, tests, mock interviews, offers, or anything else.{' '}
              If someone asks you for a fee, <Link className="link" to="/contact?topic=fraud">tell us</Link> and we act within a day.
            </div>
          </div>
        )}

        {/* FAQ — same content as before, still at the bottom */}
        <section className="mt-8">
          <FaqAccordion
            heading="Common questions"
            emitJsonLd
            items={[
              { q: 'Why don\'t you show a price on this page?',
                a: 'Kollez is priced per conversation. Institution pricing depends on how many colleges you\'re running, whether you\'re on the government / aided tier, and which add-ons you want. Book a 30-min demo and we\'ll walk out with a quote your finance office can raise a PO against.' },
              { q: 'What if we leave — do we take our data?',
                a: 'Yes. Full export in NIRF/NAAC/AICTE formats plus a JSON audit log. Soft-delete window before permanent removal.' },
              { q: 'How does the PO/GST invoice flow work?',
                a: 'Pick a plan, we send a proforma invoice with your GSTIN. Your accounts team raises a PO; we then issue a tax invoice against it. NEFT/RTGS/UPI/card all supported.' },
              { q: 'Is the government / aided-institution discount automatic?',
                a: 'Yes. Select "Government / aided" during signup and the discount comes off automatically on the proforma. No paperwork.' },
              { q: 'What is the pilot? How long does it run?',
                a: 'A free pilot covers one college for one placement season. You keep everything you post; you upgrade whenever you\'re ready.' },
              { q: 'Do students ever pay?',
                a: 'No. Students never pay on Kollez. Ever. If someone asks you to pay to apply for a job through us, that is a scam — tell us and we act within a day.' },
              { q: 'What integrations do you support?',
                a: 'DigiLocker for marks verification, Google/Microsoft SSO for institution staff, WhatsApp Business for student outreach, common ATSes (Darwinbox, Zoho Recruit, Greenhouse) for recruiters. CSV export from every screen.' },
              { q: 'How long is onboarding?',
                a: 'A pilot college is usually live in 3–4 working days. Group-wide rollout across 10+ colleges takes 2–3 weeks including MoU sign-off.' },
              { q: 'What compliance and security do you offer?',
                a: 'SOC 2 Type II, ISO 27001, DPDP-ready, data hosted in India. Every mutation is audit-logged. Every offer has sign-off checks and a 10-minute undo window. See /security for the full picture.' },
              { q: 'Can we get help with data migration from spreadsheets?',
                a: 'Yes — add the "Onboarding done for you" engagement and we\'ll import rosters, structure and partners and train your placement officers.' },
              { q: 'Do you offer refunds?',
                a: 'If Kollez doesn\'t work for your college within the first 30 days of a paid plan, we refund the unused portion. Pilot is free.' },
            ]}
          />
        </section>
        </div>
      </main>
    </div>
  );
}
